import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  BarChart3, FileText, AlertCircle, TrendingUp, DollarSign, Target, Clock,
  RefreshCw, Download, Calendar, ChevronRight, Brain, Layers, Sparkles,
  AlertTriangle, CheckCircle2, ArrowUpRight, ArrowDownRight, Filter, Play,
  Settings2, Table as TableIcon
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  AreaChart, Area, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { formatCurrency, formatConfidence } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = ['#2463EB', '#16A34A', '#DC2626', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// API functions
async function getExecutiveDashboard() {
  const response = await fetch(`${API_URL}/api/analytics/executive-dashboard`);
  if (!response.ok) throw new Error('Failed to fetch dashboard data');
  return response.json();
}

async function getDenialTrends(days = 30) {
  const response = await fetch(`${API_URL}/api/analytics/denial-trends?days=${days}`);
  if (!response.ok) throw new Error('Failed to fetch denial trends');
  return response.json();
}

async function getAppealRecommendations() {
  const response = await fetch(`${API_URL}/api/analytics/appeal-recommendations?min_probability=0.5&limit=10`);
  if (!response.ok) throw new Error('Failed to fetch appeal recommendations');
  return response.json();
}

async function getDenialModelMetrics() {
  const response = await fetch(`${API_URL}/api/analytics/denial-model-metrics`);
  if (!response.ok) throw new Error('Failed to fetch model metrics');
  return response.json();
}

async function getReportTemplates() {
  const response = await fetch(`${API_URL}/api/analytics/reports/templates`);
  if (!response.ok) throw new Error('Failed to fetch templates');
  return response.json();
}

async function buildCustomReport(params) {
  const response = await fetch(`${API_URL}/api/analytics/reports/build`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params)
  });
  if (!response.ok) throw new Error('Failed to build report');
  return response.json();
}

async function runTemplateReport(templateId, params = {}) {
  const queryParams = new URLSearchParams();
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.format) queryParams.append('format', params.format);
  
  const url = `${API_URL}/api/analytics/reports/from-template/${templateId}?${queryParams}`;
  const response = await fetch(url, { method: 'POST' });
  if (!response.ok) throw new Error('Failed to run template report');
  return response.json();
}

// Available metrics for custom report builder
const AVAILABLE_METRICS = [
  { id: 'document_count', label: 'Document Count', format: 'number' },
  { id: 'total_revenue', label: 'Total Revenue', format: 'currency' },
  { id: 'denial_count', label: 'Denial Count', format: 'number' },
  { id: 'denial_rate', label: 'Denial Rate', format: 'percentage' },
  { id: 'avg_processing_time', label: 'Avg Processing Time', format: 'seconds' },
  { id: 'confidence_score', label: 'Confidence Score', format: 'percentage' }
];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'payer', label: 'Payer' },
  { value: 'classification', label: 'Classification' },
  { value: 'queue', label: 'Queue' }
];

// KPI Card Component
function KPICard({ title, value, subtitle, trend, trendLabel, icon: Icon, color = "primary" }) {
  const isPositive = trend > 0;
  const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription className="text-xs flex items-center space-x-2">
            <Icon className="w-3 h-3" />
            <span>{title}</span>
          </CardDescription>
          {trend !== undefined && trend !== 0 && (
            <Badge variant={isPositive ? "default" : "destructive"} className="text-xs">
              <TrendIcon className="w-3 h-3 mr-1" />
              {Math.abs(trend)}%
            </Badge>
          )}
        </div>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-muted-foreground">
          {subtitle}
        </div>
        {trendLabel && (
          <div className="text-xs text-muted-foreground mt-1">
            {trendLabel}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Sparkline Mini Chart
function Sparkline({ data, dataKey, color = "#2463EB", height = 40 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id={`sparkline-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
            <stop offset="95%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey={dataKey} 
          stroke={color} 
          strokeWidth={2}
          fill={`url(#sparkline-${dataKey})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ML Prediction Card
function PredictionCard({ title, description, probability, confidence, factors }) {
  const getColor = (prob) => {
    if (prob >= 0.7) return 'text-emerald-600';
    if (prob >= 0.4) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <Brain className="w-4 h-4 text-primary" />
        </div>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-2xl font-bold ${getColor(probability)}`}>
            {(probability * 100).toFixed(0)}%
          </span>
          <Badge variant="outline" className="text-xs">
            {(confidence * 100).toFixed(0)}% confidence
          </Badge>
        </div>
        <Progress value={probability * 100} className="h-2 mb-3" />
        {factors && factors.length > 0 && (
          <div className="space-y-1">
            {factors.slice(0, 3).map((factor, idx) => (
              <div key={idx} className="text-xs text-muted-foreground flex items-start">
                <CheckCircle2 className="w-3 h-3 mr-1 mt-0.5 text-primary flex-shrink-0" />
                {factor}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Report Template Card
function ReportTemplateCard({ template, onRun, isLoading }) {
  return (
    <Card className={`hover:shadow-md transition-shadow ${isLoading ? 'opacity-70' : 'cursor-pointer'}`} onClick={() => !isLoading && onRun(template.id)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{template.name}</CardTitle>
          {isLoading ? (
            <RefreshCw className="w-4 h-4 animate-spin text-primary" />
          ) : (
            <Play className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
        <CardDescription className="text-xs">{template.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1 mb-3">
          {template.metrics.slice(0, 3).map((metric, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {metric.replace(/_/g, ' ')}
            </Badge>
          ))}
          {template.metrics.length > 3 && (
            <Badge variant="outline" className="text-xs">+{template.metrics.length - 3}</Badge>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Group by: {template.group_by}</span>
          <span>{template.default_period} days</span>
        </div>
      </CardContent>
    </Card>
  );
}

// Custom Report Builder Component
function CustomReportBuilder() {
  const [selectedMetrics, setSelectedMetrics] = useState(['document_count', 'denial_count']);
  const [groupBy, setGroupBy] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterPayer, setFilterPayer] = useState('');
  const [filterClassification, setFilterClassification] = useState('');
  const [reportResults, setReportResults] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);

  const handleMetricToggle = (metricId) => {
    setSelectedMetrics(prev => 
      prev.includes(metricId)
        ? prev.filter(m => m !== metricId)
        : [...prev, metricId]
    );
  };

  const handleBuildReport = async () => {
    if (selectedMetrics.length === 0) {
      toast.error('Please select at least one metric');
      return;
    }

    setIsBuilding(true);
    try {
      const params = {
        metrics: selectedMetrics,
        group_by: groupBy,
        format: 'json'
      };
      
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const filters = {};
      if (filterPayer) filters.payer = filterPayer;
      if (filterClassification) filters.classification = filterClassification;
      if (Object.keys(filters).length > 0) params.filters = filters;
      
      const result = await buildCustomReport(params);
      setReportResults(result);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error(`Failed to build report: ${error.message}`);
    } finally {
      setIsBuilding(false);
    }
  };

  const handleExportCSV = async () => {
    if (!reportResults?.results) {
      toast.error('No report data to export');
      return;
    }

    try {
      const params = {
        metrics: selectedMetrics,
        group_by: groupBy,
        format: 'csv'
      };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const result = await buildCustomReport(params);
      
      // Create and download CSV
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clairvoy-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error(`Failed to export CSV: ${error.message}`);
    }
  };

  const handleExportJSON = () => {
    if (!reportResults) {
      toast.error('No report data to export');
      return;
    }

    const blob = new Blob([JSON.stringify(reportResults, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clairvoy-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('JSON exported successfully!');
  };

  const formatValue = (value, metricId) => {
    const metric = AVAILABLE_METRICS.find(m => m.id === metricId);
    if (!metric) return value;
    
    switch (metric.format) {
      case 'currency':
        return formatCurrency(value);
      case 'percentage':
        return `${value?.toFixed(1) || 0}%`;
      case 'seconds':
        return `${value?.toFixed(1) || 0}s`;
      default:
        return value?.toFixed?.(0) || value || 0;
    }
  };

  return (
    <Card data-testid="custom-report-builder">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              Custom Report Builder
            </CardTitle>
            <CardDescription>Select metrics and configure your custom report</CardDescription>
          </div>
          <div className="flex gap-2">
            {reportResults && (
              <>
                <Button variant="outline" size="sm" onClick={handleExportJSON} data-testid="export-json-btn">
                  <Download className="w-4 h-4 mr-2" />
                  JSON
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
                  <Download className="w-4 h-4 mr-2" />
                  CSV
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Metric Selection */}
        <div>
          <Label className="text-sm font-medium mb-3 block">Select Metrics</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {AVAILABLE_METRICS.map((metric) => (
              <div 
                key={metric.id}
                className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedMetrics.includes(metric.id) 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => handleMetricToggle(metric.id)}
                data-testid={`metric-${metric.id}`}
              >
                <Checkbox 
                  checked={selectedMetrics.includes(metric.id)}
                  onCheckedChange={() => handleMetricToggle(metric.id)}
                />
                <span className="text-sm">{metric.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Configuration Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Group By */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Group By</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="group-by-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Start Date</Label>
            <Input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="start-date-input"
            />
          </div>

          {/* End Date */}
          <div>
            <Label className="text-sm font-medium mb-2 block">End Date</Label>
            <Input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="end-date-input"
            />
          </div>

          {/* Build Button */}
          <div className="flex items-end">
            <Button 
              onClick={handleBuildReport} 
              disabled={isBuilding || selectedMetrics.length === 0}
              className="w-full"
              data-testid="build-report-btn"
            >
              {isBuilding ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Building...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Build Report
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Optional Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Filter by Payer (optional)</Label>
            <Input 
              placeholder="Enter payer name..."
              value={filterPayer}
              onChange={(e) => setFilterPayer(e.target.value)}
              data-testid="filter-payer-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block">Filter by Classification (optional)</Label>
            <Select value={filterClassification || "all"} onValueChange={(val) => setFilterClassification(val === "all" ? "" : val)}>
              <SelectTrigger data-testid="filter-classification-select">
                <SelectValue placeholder="All classifications" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Payment">Payment</SelectItem>
                <SelectItem value="Denial">Denial</SelectItem>
                <SelectItem value="Adjustment">Adjustment</SelectItem>
                <SelectItem value="Recoupment">Recoupment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Table */}
        {reportResults && reportResults.results && reportResults.results.length > 0 && (
          <div className="border rounded-lg overflow-hidden" data-testid="report-results">
            <div className="bg-muted/50 px-4 py-2 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TableIcon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Report Results</span>
              </div>
              <Badge variant="secondary">{reportResults.results.length} rows</Badge>
            </div>
            <div className="max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Group</TableHead>
                    {selectedMetrics.map(metricId => {
                      const metric = AVAILABLE_METRICS.find(m => m.id === metricId);
                      return <TableHead key={metricId}>{metric?.label || metricId}</TableHead>;
                    })}
                    {reportResults.results[0]?.denial_rate !== undefined && !selectedMetrics.includes('denial_rate') && (
                      <TableHead>Denial Rate</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportResults.results.map((row, idx) => (
                    <TableRow key={idx} className="hover:bg-muted/30">
                      <TableCell className="font-medium">{row.group}</TableCell>
                      {selectedMetrics.map(metricId => (
                        <TableCell key={metricId}>{formatValue(row[metricId], metricId)}</TableCell>
                      ))}
                      {row.denial_rate !== undefined && !selectedMetrics.includes('denial_rate') && (
                        <TableCell>{row.denial_rate}%</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {reportResults.summary && (
              <div className="bg-muted/50 px-4 py-3 border-t">
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">Summary: </span>
                  {selectedMetrics.map(m => {
                    const total = reportResults.summary[`${m}_total`];
                    const metric = AVAILABLE_METRICS.find(mt => mt.id === m);
                    if (total === undefined) return null;
                    return (
                      <span key={m} className="mr-4">
                        {metric?.label}: {formatValue(total, m)}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {reportResults && reportResults.results && reportResults.results.length === 0 && (
          <div className="text-center py-8 border rounded-lg bg-muted/20">
            <TableIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No data found for the selected criteria</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [trendPeriod, setTrendPeriod] = useState('30');
  const [runningTemplateId, setRunningTemplateId] = useState(null);
  const [templateResults, setTemplateResults] = useState(null);
  const queryClient = useQueryClient();
  
  const { data: dashboard, isLoading: loadingDashboard, refetch: refetchDashboard } = useQuery({
    queryKey: ['executive-dashboard'],
    queryFn: getExecutiveDashboard,
    refetchInterval: 60000 // Refresh every minute
  });

  const { data: trends } = useQuery({
    queryKey: ['denial-trends', trendPeriod],
    queryFn: () => getDenialTrends(parseInt(trendPeriod)),
  });

  const { data: appealRecs } = useQuery({
    queryKey: ['appeal-recommendations'],
    queryFn: getAppealRecommendations,
  });

  const { data: modelMetrics } = useQuery({
    queryKey: ['denial-model-metrics'],
    queryFn: getDenialModelMetrics,
  });

  const { data: templates } = useQuery({
    queryKey: ['report-templates'],
    queryFn: getReportTemplates,
  });

  if (loadingDashboard) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-muted rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const stats = dashboard || {};
  const revenue = stats.revenue || {};
  const denials = stats.denials || {};
  const processing = stats.processing || {};
  const appeals = stats.appeals || {};
  const sla = stats.sla || {};

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-heading">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Executive overview with analytics and ML insights</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <Select value={trendPeriod} onValueChange={setTrendPeriod}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => refetchDashboard()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="executive" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="executive" data-testid="tab-executive">Overview</TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial">Financial</TabsTrigger>
          <TabsTrigger value="denials" data-testid="tab-denials">Denials</TabsTrigger>
          <TabsTrigger value="predictions" data-testid="tab-predictions">ML Predictions</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        {/* EXECUTIVE OVERVIEW TAB */}
        <TabsContent value="executive" className="space-y-6 mt-6">
          {/* Revenue KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard
              title="Revenue (YTD)"
              value={formatCurrency(revenue.ytd || 0)}
              subtitle={`MTD: ${formatCurrency(revenue.mtd || 0)} • QTD: ${formatCurrency(revenue.qtd || 0)}`}
              trend={revenue.mtd_growth}
              trendLabel={`${revenue.ytd_count || 0} documents processed`}
              icon={DollarSign}
            />
            <KPICard
              title="Denial Rate"
              value={`${denials.rate || 0}%`}
              subtitle={`${denials.count || 0} denials • ${formatCurrency(denials.total_amount || 0)}`}
              icon={AlertCircle}
            />
            <KPICard
              title="Appeal Success Rate"
              value={`${appeals.success_rate || 0}%`}
              subtitle={`Recovered: ${formatCurrency(appeals.revenue_recovered || 0)}`}
              trendLabel={`${appeals.total_appeals || 0} total appeals`}
              icon={Target}
            />
            <KPICard
              title="SLA Compliance"
              value={`${sla.compliance_rate || 100}%`}
              subtitle={`${sla.overdue || 0} overdue • ${sla.at_risk || 0} at risk`}
              icon={Clock}
            />
          </div>

          {/* Processing Stats & Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Processing Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Processing Performance</CardTitle>
                <CardDescription>Average processing metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Avg Processing Time</span>
                  <span className="font-bold">{processing.avg_processing_time?.toFixed(1) || 0}s</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Throughput</span>
                  <span className="font-bold">{processing.throughput_per_hour?.toFixed(1) || 0} docs/hr</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Processed (MTD)</span>
                  <span className="font-bold">{processing.total_processed || 0}</span>
                </div>
                {processing.by_document_type && processing.by_document_type.length > 0 && (
                  <div className="pt-3 border-t">
                    <div className="text-xs font-medium text-muted-foreground mb-2">By Document Type</div>
                    {processing.by_document_type.slice(0, 3).map((type, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-1">
                        <span className="truncate">{type._id}</span>
                        <span className="text-muted-foreground">{type.avg_time?.toFixed(1)}s</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Volume Trend Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Processing Volume Trend</CardTitle>
                <CardDescription>Documents processed over time</CardDescription>
              </CardHeader>
              <CardContent>
                {stats.volume_trend && stats.volume_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={stats.volume_trend}>
                      <defs>
                        <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2463EB" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#2463EB" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="uploaded" stroke="#2463EB" fill="url(#volumeGradient)" name="Uploaded" />
                      <Area type="monotone" dataKey="processed" stroke="#16A34A" fill="none" name="Processed" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                    No volume data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Denial Trend Chart */}
          {trends && trends.trends && trends.trends.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Denial Rate Trend</CardTitle>
                    <CardDescription>Daily denial percentage over selected period</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={trends.trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="total_documents" fill="#2463EB" name="Total Documents" opacity={0.7} />
                    <Bar yAxisId="left" dataKey="denials" fill="#DC2626" name="Denials" opacity={0.7} />
                    <Line yAxisId="right" type="monotone" dataKey="denial_rate" stroke="#F59E0B" strokeWidth={2} name="Denial Rate (%)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Payer Performance */}
          {stats.payer_performance && stats.payer_performance.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Payers by Volume</CardTitle>
                <CardDescription>Revenue and performance metrics by payer</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={stats.payer_performance.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="_id" type="category" tick={{ fontSize: 10 }} width={150} />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'document_count' ? value : `${value.toFixed(1)}%`,
                        name === 'document_count' ? 'Documents' : 'Denial Rate'
                      ]}
                    />
                    <Legend />
                    <Bar dataKey="document_count" fill="#2463EB" name="Documents" />
                    <Bar dataKey="denial_rate" fill="#DC2626" name="Denial Rate %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* FINANCIAL TAB */}
        <TabsContent value="financial" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard
              title="Total Revenue (YTD)"
              value={formatCurrency(revenue.ytd || 0)}
              subtitle={`${revenue.ytd_count || 0} documents`}
              icon={DollarSign}
            />
            <KPICard
              title="Denial Write-offs"
              value={formatCurrency(denials.total_amount || 0)}
              subtitle={`${denials.count || 0} non-recoverable denials`}
              icon={AlertTriangle}
            />
            <KPICard
              title="Appeal Recovery"
              value={formatCurrency(appeals.revenue_recovered || 0)}
              subtitle={`${appeals.success_rate || 0}% success rate`}
              icon={TrendingUp}
            />
          </div>

          {/* Payer Revenue Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue by Payer</CardTitle>
              <CardDescription>Detailed payer financial performance</CardDescription>
            </CardHeader>
            <CardContent>
              {stats.payer_performance && stats.payer_performance.length > 0 ? (
                <div className="space-y-3">
                  {stats.payer_performance.map((payer, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{payer._id}</div>
                        <div className="text-xs text-muted-foreground">
                          {payer.document_count} documents • {payer.denial_rate?.toFixed(1)}% denial rate
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{formatCurrency(payer.total_amount || 0)}</div>
                        <div className="text-xs text-muted-foreground">
                          Confidence: {formatConfidence(payer.avg_confidence)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No payer data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DENIALS TAB */}
        <TabsContent value="denials" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard
              title="Total Denials"
              value={denials.count || 0}
              subtitle="This month"
              icon={AlertCircle}
            />
            <KPICard
              title="Denial Amount"
              value={formatCurrency(denials.total_amount || 0)}
              subtitle="Total denied value"
              icon={DollarSign}
            />
            <KPICard
              title="Pending Appeals"
              value={appeals.pending_count || 0}
              subtitle={formatCurrency(appeals.pending_amount || 0)}
              icon={FileText}
            />
            <KPICard
              title="Recovery Rate"
              value={`${appeals.success_rate || 0}%`}
              subtitle={formatCurrency(appeals.revenue_recovered || 0) + " recovered"}
              icon={Target}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Denial Reasons</CardTitle>
              <CardDescription>Most common denial codes</CardDescription>
            </CardHeader>
            <CardContent>
              {denials.top_reasons && denials.top_reasons.length > 0 ? (
                <div className="space-y-3">
                  {denials.top_reasons.map((reason, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Badge variant="outline" className="font-mono">{reason._id || 'N/A'}</Badge>
                        <div>
                          <div className="text-sm font-medium">{reason.description || `Code ${reason._id}`}</div>
                          <div className="text-xs text-muted-foreground">{reason.count} occurrences</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{formatCurrency(reason.total_amount || 0)}</div>
                        <Badge variant="secondary" className="text-xs mt-1">{reason.category || 'Other'}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No denial data available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ML PREDICTIONS TAB */}
        <TabsContent value="predictions" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Model Performance Card */}
            <Card className="md:col-span-1">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg">Model Performance</CardTitle>
                </div>
                <CardDescription>Denial prediction model metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {modelMetrics && modelMetrics.metrics ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Accuracy</span>
                      <div className="flex items-center gap-2">
                        <Progress value={(modelMetrics.metrics.accuracy || 0) * 100} className="w-20 h-2" />
                        <span className="font-bold">{((modelMetrics.metrics.accuracy || 0) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Precision</span>
                      <div className="flex items-center gap-2">
                        <Progress value={(modelMetrics.metrics.precision || 0) * 100} className="w-20 h-2" />
                        <span className="font-bold">{((modelMetrics.metrics.precision || 0) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Recall</span>
                      <div className="flex items-center gap-2">
                        <Progress value={(modelMetrics.metrics.recall || 0) * 100} className="w-20 h-2" />
                        <span className="font-bold">{((modelMetrics.metrics.recall || 0) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="pt-3 border-t text-xs text-muted-foreground">
                      <div>Training samples: {modelMetrics.training_samples}</div>
                      <div>Test samples: {modelMetrics.test_samples}</div>
                      <div>Model version: {modelMetrics.model_version}</div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <Brain className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Loading model metrics...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Appeal Recommendations */}
            <Card className="md:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg">AI Appeal Recommendations</CardTitle>
                  </div>
                  <Badge variant="secondary">ML-Powered</Badge>
                </div>
                <CardDescription>Denials with highest appeal success probability</CardDescription>
              </CardHeader>
              <CardContent>
                {appealRecs && appealRecs.recommendations && appealRecs.recommendations.length > 0 ? (
                  <div className="space-y-3">
                    {appealRecs.recommendations.slice(0, 5).map((rec, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Progress value={rec.success_probability * 100} className="w-16 h-2" />
                            <span className="text-sm font-bold text-emerald-600">
                              {(rec.success_probability * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Denial: {formatCurrency(rec.denial_amount)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-emerald-600">
                            {formatCurrency(rec.expected_recovery)}
                          </div>
                          <div className="text-xs text-muted-foreground">Expected recovery</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No appeal recommendations available</p>
                    <p className="text-xs mt-1">Upload more documents to generate predictions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Prediction Features Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Denial Prediction
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                Predicts denial probability for new claims based on payer history, 
                service codes, and amount patterns.
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-500" />
                  Appeal Success
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                Recommends which denials to appeal based on historical success rates
                by category, payer, and amount.
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  Anomaly Detection
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                Identifies unusual patterns in processing times, amounts, 
                and extraction confidence scores.
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* REPORTS TAB */}
        <TabsContent value="reports" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Report Templates
              </CardTitle>
              <CardDescription>Click a template to generate a report instantly</CardDescription>
            </CardHeader>
            <CardContent>
              {templates && templates.templates ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.templates.map((template) => (
                    <ReportTemplateCard
                      key={template.id}
                      template={template}
                      isLoading={runningTemplateId === template.id}
                      onRun={async (id) => {
                        setRunningTemplateId(id);
                        try {
                          const result = await runTemplateReport(id);
                          setTemplateResults(result);
                          toast.success(`${template.name} generated successfully!`);
                        } catch (error) {
                          toast.error(`Failed to run template: ${error.message}`);
                        } finally {
                          setRunningTemplateId(null);
                        }
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Loading templates...
                </div>
              )}
            </CardContent>
          </Card>

          {/* Template Results */}
          {templateResults && templateResults.results && templateResults.results.length > 0 && (
            <Card data-testid="template-results">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TableIcon className="w-5 h-5 text-primary" />
                      {templateResults.template_name || 'Template Report'}
                    </CardTitle>
                    <CardDescription>
                      Generated at {new Date(templateResults.generated_at).toLocaleString()}
                    </CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setTemplateResults(null)}>
                    Clear
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead>Group</TableHead>
                          {Object.keys(templateResults.results[0] || {}).filter(k => k !== 'group').map(key => (
                            <TableHead key={key}>{key.replace(/_/g, ' ')}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {templateResults.results.map((row, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/30">
                            <TableCell className="font-medium">{row.group}</TableCell>
                            {Object.entries(row).filter(([k]) => k !== 'group').map(([key, value]) => (
                              <TableCell key={key}>
                                {typeof value === 'number' 
                                  ? (key.includes('rate') || key.includes('confidence') 
                                      ? `${value.toFixed(1)}%` 
                                      : key.includes('revenue') || key.includes('amount')
                                        ? formatCurrency(value)
                                        : value.toFixed?.(1) || value)
                                  : value}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custom Report Builder */}
          <CustomReportBuilder />
        </TabsContent>
      </Tabs>
    </div>
  );
}
